import { useCallback, useMemo, useState } from 'react';
import { FileSearch } from 'lucide-react';
import { createRepeatedTaxRecalculator, useRepeatRowsHandler } from '@/lib/table-handlers';
import { calculateTotals, handleUpdateRowFields, transformFormData } from '../../utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AritoFormV2, FormHeaderActionsV2 } from '@/components/custom/erp';
import { getDocumentsColumns } from './get-documents-columns';
import { formSchema, initialFormValues } from '../../schema';
import { useDetailRows } from './DetailTab/useDetailRows';
import { useAuth } from '@/contexts/auth-context';
import CustomerInfoTab from './CustomerInfoTab';
import { useFormFieldState } from '../../hooks';
import InfoDropdown from './InfoDropdown';
import BasicInfoTab from './BasicInfoTab';
import { HistoryTab } from './HistoryTab';
import EInvoiceTab from './EInvoiceTab';
import BottomBar from './BottomBar';

import GetDocumentsInvoice from '@/components/custom/erp/get-documents-v2';
import { useDonViCoSo } from '@/hooks';
import DetailTab from './DetailTab';
import OtherTab from './OtherTab';

interface FormDialogProps {
  formMode: 'add' | 'edit' | 'view';
  initialData: BanHangRecord;
  onClose: () => void;
  onSubmit: (data: BanHangPayload) => void;
}

const recalculateTaxAfterRepeat = createRepeatedTaxRecalculator<BanHangRecord>({
  getTaxBase: row => Number(row?.tien_nt2 || 0)
});

export default function FormDialog({ formMode, initialData, onClose, onSubmit }: FormDialogProps) {
  const { entityUnit } = useAuth();
  const { state, actions } = useFormFieldState(initialData);
  const [activeTab, setActiveTab] = useState<string>('info');
  const [openDialog, setOpenDialog] = useState('none');

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);
  const { donViCoSos } = useDonViCoSo();
  const {
    rows,
    setRows,
    selectedRowUuid,
    selectedCell,
    handleRowClick,
    handleCellClick,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    handleCellValueChange,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs
  } = useDetailRows(initialData?.chi_tiet || [], undefined, handleUpdateRowFields, state);

  const { tong_tien, thue } = useMemo(() => {
    return calculateTotals(rows);
  }, [rows]);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setRows,
    markDirty: () => setOpenDialog(prev => (prev === 'dirty' ? prev : 'dirty')),
    onAfterRepeat: recalculateTaxAfterRepeat
  });

  const handleSubmit = (data: BanHangPayload) => {
    const formattedData = transformFormData(data, entityUnit, state, rows, tong_tien, thue);
    onSubmit({ ...formattedData, unit_id: donViCoSos[0]?.uuid || '' });
  };

  const handleSearchData = (searchedData: BanHangPayload) => {
    const { xprocess, customer, selectedRows } = searchedData;

    if (customer) {
      actions.setKhachHang(customer);
    }
    const updatedRows = selectedRows.map((row: BanHangRecord, index: number) => ({
      ...row,
      uuid: `row-${row.uuid}`,
      line: index + 1,
      tk_no_data: row.tk_dt_data,
      tk_thue_no_data: row.tk_thue_co_data,
      id_hd: row.hoa_don_data,
      line_hd: {
        uuid: row.uuid,
        line: row.line
      }
    }));

    let mapRows = [];
    if (xprocess === '1') mapRows = [...rows, ...updatedRows];
    else mapRows = updatedRows;

    setRows(mapRows);
  };

  return (
    <>
      <AritoFormV2
        mode={formMode}
        title={formMode === 'add' ? 'Mới' : undefined}
        subTitle='Hóa đơn dịch vụ trả lại, giảm giá'
        schema={formSchema}
        initialData={initialData || initialFormValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={
          <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
            <div className='flex items-stretch justify-between border-b border-[#d7d7d7] bg-muted'>
              <TabsList className='h-8 w-full justify-start rounded-none border-0 bg-muted p-0 text-slate-600'>
                <TabsTrigger
                  value='info'
                  className='relative inline-flex h-8 items-center justify-center rounded-none border-0 bg-transparent px-4 py-0 text-[12px] font-semibold leading-none text-slate-500 shadow-none ring-0 transition-colors after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:bg-main after:opacity-0 hover:bg-transparent hover:text-main hover:after:opacity-100 focus-visible:ring-0 data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none data-[state=active]:after:opacity-100'
                >
                  <span className='translate-y-px'>Thông tin</span>
                </TabsTrigger>
                {formMode === 'view' && (
                  <TabsTrigger
                    value='history'
                    className='relative inline-flex h-8 items-center justify-center rounded-none border-0 bg-transparent px-4 py-0 text-[12px] font-semibold leading-none text-slate-500 shadow-none ring-0 transition-colors after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:bg-main after:opacity-0 hover:bg-transparent hover:text-main hover:after:opacity-100 focus-visible:ring-0 data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none data-[state=active]:after:opacity-100'
                  >
                    <span className='translate-y-px'>Lịch sử</span>
                  </TabsTrigger>
                )}
              </TabsList>
              <FormHeaderActionsV2
                mode={formMode}
                className='-mr-1'
                source={{
                  label: 'Hóa đơn bán dịch vụ',
                  onClick: () => setOpenDialog('get-documents')
                }}
                info={<InfoDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />}
              />
            </div>
            <TabsContent value='info' className='mt-0 outline-none'>
              <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
            </TabsContent>
            {formMode === 'view' && (
              <TabsContent value='history' className='mt-0 outline-none'>
                <HistoryTab />
              </TabsContent>
            )}
          </Tabs>
        }
        tabs={
          activeTab === 'info'
            ? [
                {
                  id: 'details',
                  label: 'Chi tiết',
                  component: (
                    <DetailTab
                      rows={rows}
                      selectedRowUuid={selectedRowUuid}
                      selectedCell={selectedCell}
                      handleRowClick={handleRowClick}
                      onCellClick={handleCellClick}
                      handleAddRow={handleAddRow}
                      handleDeleteRow={handleDeleteRow}
                      handleCopyRow={handleCopyRow}
                      handlePasteRow={handlePasteRow}
                      handleMoveRow={handleMoveRow}
                      onRepeatForRows={handleRepeatDetailRows}
                      handleCellValueChange={handleCellValueChange}
                      formMode={formMode}
                      dienGiai={state.dienGiai}
                      handleUpdateNonWritableFields={detailSetNonWritableFields}
                      handleUpdatePasteConfigs={detailSetPasteConfigs}
                      constraints={state.taiKhoan?.constraints ?? state.khachHang?.account_data?.constraints ?? []}
                    />
                  )
                },
                {
                  id: 'customer-info',
                  label: 'Thông tin khách hàng',
                  component: <CustomerInfoTab formMode={formMode} formState={{ state, actions }} />
                },
                {
                  id: 'e-invoice',
                  label: 'HĐĐT(Không sử dụng)',
                  component: <EInvoiceTab formMode={formMode} />
                },
                {
                  id: 'other',
                  label: 'Khác',
                  component: <OtherTab formMode={formMode} formState={{ state, actions }} />
                }
              ]
            : []
        }
        bottomBar={
          activeTab === 'info' ? <BottomBar tong_tien={tong_tien} thue={thue} ty_gia={state.ty_gia} /> : undefined
        }
      />
      {openDialog === 'get-documents' && (
        <GetDocumentsInvoice
          onSearch={handleSearchData}
          onClose={() => setOpenDialog('none')}
          chungTuType={'HDDV'}
          detailColumns={getDocumentsColumns()}
        />
      )}
    </>
  );
}
